/**
 * Сетка расписания (manager design v3 §8.1): сотрудники × дни недели,
 * цветные ячейки смен. Собирается из готового бэка:
 *   - отделы (staff/departments) + сотрудники (staff/members?department=)
 *   - графики (scheduling/schedules?department=) + смены (schedules/{id}/shifts)
 * Чистый server-state через React Query, без useEffect-фетча (патент).
 */
import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { listScheduleShifts, listSchedules, listStaffMembers } from '@hsc/api-client';
import type { Schedule, Shift, StaffDepartment, StaffMember } from '@hsc/types';
import { useStaffDepartments } from '../staff/useStaff.js';

export interface GridStaff {
  id: string;
  label: string;
  departmentCode: string;
}

export interface GridShift {
  shift: Shift;
  /** Индекс дня недели 0..6 (Пн..Вс) внутри отображаемой недели. */
  dayIndex: number;
}

/** Понедельник недели, содержащей date (локальное время, 00:00). */
export function weekStart(date: Date): Date {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const dow = (d.getDay() + 6) % 7; // Пн=0
  d.setDate(d.getDate() - dow);
  return d;
}

export function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

const DEPT_SHORT: Readonly<Record<string, string>> = {
  front_desk: 'ресепшн',
  housekeeping: 'хаускип',
  kitchen: 'кухня',
  bar: 'бар',
  f_and_b: 'F&B',
  guest_services: 'сервис',
  maintenance: 'инж',
  spa: 'спа',
};

function deptShort(code?: string): string {
  if (!code) return '';
  return DEPT_SHORT[code] ?? code;
}

function staffLabel(m: StaffMember, deptCode?: string): string {
  // Имя+фамилия из platform.users_identity (бэк уже резолвит display_name в
  // /v1/staff/members). Должность — только запасной вариант, когда у строки
  // нет user_id и имя не резолвится.
  const name = m.display_name?.trim() || m.position_title?.trim() || 'Сотрудник';
  const short = deptShort(deptCode);
  return short ? `${name} · ${short}` : name;
}

export interface ScheduleGridData {
  weekFrom: Date;
  days: Date[];
  staff: GridStaff[];
  /** shifts по staffId. */
  byStaff: Map<string, GridShift[]>;
  departments: StaffDepartment[];
  totalShifts: number;
  isLoading: boolean;
  isError: boolean;
}

/**
 * @param weekFrom — понедельник отображаемой недели.
 * @param departmentId — фильтр по отделу (undefined = все отделы).
 */
export function useScheduleGrid(weekFrom: Date, departmentId?: string): ScheduleGridData {
  const deptsQuery = useStaffDepartments();
  // Кандидаты на запрос сотрудников — только НЕ архивные отделы (archived
  // не показываем в фильтре расписания и не дёргаем по ним персонал).
  const candidateDepartments = useMemo<StaffDepartment[]>(
    () => (deptsQuery.data ?? []).filter((d) => d.status !== 'archived'),
    [deptsQuery.data],
  );

  // Сотрудники по КАЖДОМУ отделу-кандидату (нужно знать, в каких отделах есть
  // персонал — иначе фильтр покажет пустые отделы). Запросы кэшируются тем же
  // ключом, что и в модуле «Персонал», так что без дубль-сети.
  const memberQueries = useQueries({
    queries: candidateDepartments.map((d) => ({
      queryKey: ['staff', 'members', d.id] as const,
      queryFn: async () => (await listStaffMembers(d.id)).items,
      staleTime: 60_000,
      enabled: deptsQuery.isSuccess,
    })),
  });

  // Активные сотрудники по отделу + отделы, где есть хотя бы один активный
  // сотрудник (это и есть список чипов фильтра).
  const { activeMembersByDept, staffedDepartments } = useMemo(() => {
    const byDept = new Map<string, StaffMember[]>();
    const staffed: StaffDepartment[] = [];
    candidateDepartments.forEach((d, i) => {
      const members = (memberQueries[i]?.data ?? []).filter((m) => m.is_active);
      byDept.set(d.id, members);
      if (members.length > 0) staffed.push(d);
    });
    return { activeMembersByDept: byDept, staffedDepartments: staffed };
  }, [candidateDepartments, memberQueries]);

  // Видимые отделы: при активном фильтре — только выбранный (если в нём есть
  // люди), иначе все отделы с персоналом.
  const visibleDepartments = useMemo(
    () =>
      departmentId === undefined
        ? staffedDepartments
        : staffedDepartments.filter((d) => d.id === departmentId),
    [staffedDepartments, departmentId],
  );

  // Графики по каждому видимому отделу.
  const scheduleQueries = useQueries({
    queries: visibleDepartments.map((d) => ({
      queryKey: ['scheduling', 'schedules', d.id] as const,
      queryFn: async () => (await listSchedules(d.id)).items,
      staleTime: 30_000,
      enabled: deptsQuery.isSuccess,
    })),
  });

  const allSchedules = useMemo<Schedule[]>(
    () => scheduleQueries.flatMap((q) => q.data ?? []),
    [scheduleQueries],
  );

  // Смены по каждому графику.
  const shiftQueries = useQueries({
    queries: allSchedules.map((s) => ({
      queryKey: ['scheduling', 'shifts', s.id] as const,
      queryFn: async () => (await listScheduleShifts(s.id)).items,
      staleTime: 30_000,
    })),
  });

  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekFrom, i)),
    [weekFrom],
  );

  const result = useMemo<{ staff: GridStaff[]; byStaff: Map<string, GridShift[]>; total: number }>(
    () => {
      const staff: GridStaff[] = [];
      for (const d of visibleDepartments) {
        const members = activeMembersByDept.get(d.id) ?? [];
        for (const m of members) {
          staff.push({ id: m.id, label: staffLabel(m, d.code), departmentCode: d.code ?? '' });
        }
      }

      const weekEnd = addDays(weekFrom, 7);
      const byStaff = new Map<string, GridShift[]>();
      let total = 0;
      for (const q of shiftQueries) {
        for (const sh of q.data ?? []) {
          const start = new Date(sh.scheduled_start);
          if (Number.isNaN(start.getTime())) continue;
          if (start < weekFrom || start >= weekEnd) continue;
          const dayIndex = Math.floor(
            (new Date(start.getFullYear(), start.getMonth(), start.getDate()).getTime() -
              weekFrom.getTime()) /
              86_400_000,
          );
          if (dayIndex < 0 || dayIndex > 6) continue;
          const list = byStaff.get(sh.staff_member_id) ?? [];
          list.push({ shift: sh, dayIndex });
          byStaff.set(sh.staff_member_id, list);
          total += 1;
        }
      }
      return { staff, byStaff, total };
    },
    [visibleDepartments, activeMembersByDept, shiftQueries, weekFrom],
  );

  const isLoading =
    deptsQuery.isLoading ||
    memberQueries.some((q) => q.isLoading) ||
    scheduleQueries.some((q) => q.isLoading) ||
    shiftQueries.some((q) => q.isLoading);
  const isError =
    deptsQuery.isError ||
    memberQueries.some((q) => q.isError) ||
    scheduleQueries.some((q) => q.isError) ||
    shiftQueries.some((q) => q.isError);

  return {
    weekFrom,
    days,
    staff: result.staff,
    byStaff: result.byStaff,
    departments: staffedDepartments,
    totalShifts: result.total,
    isLoading,
    isError,
  };
}
